import { useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateExpenseReq,
  toUpdateExpenseReq,
  type ExpenseFormData,
} from "src/api/adapters/expense";
import {
  notEmpty,
  validate,
  test,
  type ValidationMapperFunc,
  greaterThanMinAmount,
  isPastOrCurrentDate,
  lessThanMaxAmount,
} from "src/utils";
import {
  ExpenseFields,
  FormTable,
  FormTableBody,
  MoneySecurityIcon,
} from "@porchpass/ui";
import { DomainExpenseType } from "@porchpass/api-client";
import {
  expenseErrorsAfterSubmit,
  fromUiExpensePatch,
  toUiExpense,
} from "src/forms/adapters/expense";

const validationMapper: ValidationMapperFunc<ExpenseFormData> = (formData) => ({
  amount: [
    test(notEmpty, "Must enter an amount"),
    test(greaterThanMinAmount(0), "Must be greater than 0"),
    test(lessThanMaxAmount(100_000_000), "Must be less than $100M"),
  ],
  to_month: [test(notEmpty, "Must select a month")],
  to_year: [
    test(notEmpty, "Must select a year"),
    test(
      () =>
        !isPastOrCurrentDate(formData.to_month ?? "", formData.to_year ?? ""),
      "Please select a future date",
    ),
  ],
});

type AlimonyModalProps = {
  expense: Partial<ExpenseFormData>;
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  expense,
  closeHandler,
  confirmHandler,
}: AlimonyModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<ExpenseFormData>(
    { ...expense },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: ExpenseFormData) => {
        if (expense.id) {
          return api.updateExpense(expense.id, toUpdateExpenseReq(data));
        }
        return api.createExpense(toCreateExpenseReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const formErrors = validate<ExpenseFormData>(formData, validationMapper);

  const expenseValue = useMemo(() => {
    const ui = toUiExpense(formData);
    ui.type = DomainExpenseType.ExpenseAlimony;
    return ui;
  }, [formData]);

  const fieldErrors = useMemo(
    () => expenseErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyExpenseChange = (patch: Partial<typeof expenseValue>) => {
    const mapped = fromUiExpensePatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      updateField(key as keyof ExpenseFormData)(val);
    }
  };

  return (
    <SlideUpModal
      title="Alimony/Maintenance"
      isOpen={true}
      closeHandler={closeHandler}
      hasDismissButton={true}
      actionButtonText={expense?.id ? "Save" : "Add expense"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Expense Details"
        headerIcon={
          <MoneySecurityIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#017C3F]"
          />
        }
      >
        <FormTableBody>
          <ExpenseFields
            expense={expenseValue}
            showEndDate
            errors={fieldErrors}
            onFieldChange={(field, value) => {
              applyExpenseChange({ [field]: value });
            }}
            onBulkChange={applyExpenseChange}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
